import { useState, type JSX } from "react";
import { createFileRoute, useParams, useRouter } from "@tanstack/react-router";
import { graphql } from "@repo/graphql-types/gql";
import CheckCircle from "@repo/icons/default/CheckCircle";
import Left from "@repo/icons/default/Left";
import Trash from "@repo/icons/default/Trash";

import {
  MessageDrawerActionButton,
  MessageDrawerActions,
  MessageDrawerBody,
  MessageDrawerRoot,
  MessageDrawerTitle,
} from "@/components/ui/drawer/message-drawer";
import { AsyncDataWrapper } from "@/components/ui/async-data-wrapper";
import {
  HeaderButton,
  HeaderRoot,
  HeaderTitle,
} from "@/components/ui/layout/header/header";
import { Page } from "@/components/ui/layout/page/page";
import { VehicleForm, type VehicleFormValues } from "@/components/vehicles/vehicle-form";
import {
  useGraphQL,
  useGraphQLMutationWithErrorHandler,
  useInvalidateQuery,
} from "@/hooks/use-graphql";

const VehicleByIdQuery = graphql(/* GraphQL */ `
  query VehicleByIdQuery($codUsuarioVeiculo: Int!) {
    usuariosVeiculos_by_pk(codUsuarioVeiculo: $codUsuarioVeiculo) {
      codUsuarioVeiculo
      marca
      modelo
      cor
      placa
    }
  }
`);

const UpdateVehicleMutation = graphql(/* GraphQL */ `
  mutation UpdateVehicleMutation(
    $codUsuarioVeiculo: Int!
    $set: usuariosVeiculos_set_input!
  ) {
    update_usuariosVeiculos_by_pk(
      pk_columns: { codUsuarioVeiculo: $codUsuarioVeiculo }
      _set: $set
    ) {
      codUsuarioVeiculo
    }
  }
`);

const VehiclesPageQueryDocument = graphql(/* GraphQL */ `
  query VehiclesPageQueryDocumentForEdit {
    usuariosVeiculos(where: { dataDesativacao: { _is_null: true } }) {
      codUsuarioVeiculo
    }
  }
`);

const VehicleEditPage = (): JSX.Element => {
  const { vehicleId } = useParams({ from: "/settings/profile/vehicles/$vehicleId" });
  const router = useRouter();
  const [showSuccessDrawer, setShowSuccessDrawer] = useState(false);
  const [showDeleteDrawer, setShowDeleteDrawer] = useState(false);

  const queryResult = useGraphQL(VehicleByIdQuery, {
    codUsuarioVeiculo: Number(vehicleId),
  });
  const { data } = queryResult;

  const invalidateVehiclesQuery = useInvalidateQuery(VehiclesPageQueryDocument);

  const { mutateAsync, isPending } =
    useGraphQLMutationWithErrorHandler(UpdateVehicleMutation);

  const vehicle = data?.usuariosVeiculos_by_pk;

  const handleSubmit = async (formData: VehicleFormValues): Promise<void> => {
    await mutateAsync(
      {
        codUsuarioVeiculo: Number(vehicleId),
        set: {
          marca: formData.marca,
          modelo: formData.modelo,
          cor: formData.cor,
          placa: formData.placa,
        },
      },
      {
        onSuccess: () => {
          invalidateVehiclesQuery();
          setShowSuccessDrawer(true);
        },
      },
    );
  };

  const handleCloseSuccessDrawer = (): void => {
    setShowSuccessDrawer(false);
    router.history.back();
  };

  const handleDelete = async (): Promise<void> => {
    await mutateAsync(
      {
        codUsuarioVeiculo: Number(vehicleId),
        set: {
          dataDesativacao: new Date().toISOString().split("T")[0],
        },
      },
      {
        onSuccess: () => {
          invalidateVehiclesQuery();
          setShowDeleteDrawer(false);
          router.history.back();
        },
      },
    );
  };

  return (
    <>
      <HeaderRoot>
        <HeaderButton icon={Left} align="start" />
        <HeaderTitle title="Veículos" align="center" />
        <HeaderButton
          icon={Trash}
          align="end"
          onClick={() => setShowDeleteDrawer(true)}
        />
      </HeaderRoot>
      <Page>
        <AsyncDataWrapper {...queryResult}>
          {vehicle ? (
            <VehicleForm
              defaultValues={{
                marca: vehicle.marca,
                modelo: vehicle.modelo,
                cor: vehicle.cor ?? "",
                placa: vehicle.placa,
              }}
              onSubmit={handleSubmit}
              submitLabel="Salvar"
              isEditMode
            />
          ) : null}
        </AsyncDataWrapper>
      </Page>

      <MessageDrawerRoot
        icon={CheckCircle}
        dismissible
        setOpen={setShowSuccessDrawer}
        open={showSuccessDrawer}
        variant="secondary"
      >
        <MessageDrawerTitle>Veículo atualizado com sucesso!</MessageDrawerTitle>
        <MessageDrawerActions>
          <MessageDrawerActionButton onClick={handleCloseSuccessDrawer}>
            Ok, entendi
          </MessageDrawerActionButton>
        </MessageDrawerActions>
      </MessageDrawerRoot>

      <MessageDrawerRoot
        icon={Trash}
        dismissible
        setOpen={setShowDeleteDrawer}
        open={showDeleteDrawer}
        variant="secondary"
      >
        <MessageDrawerTitle>Excluir veículo</MessageDrawerTitle>
        <MessageDrawerBody>
          Tem certeza de que deseja excluir este veículo? Esta ação não poderá ser
          desfeita.
        </MessageDrawerBody>
        <MessageDrawerActions>
          <MessageDrawerActionButton
            onClick={() => void handleDelete()}
            disabled={isPending}
          >
            {isPending ? "Excluindo..." : "Excluir"}
          </MessageDrawerActionButton>
        </MessageDrawerActions>
      </MessageDrawerRoot>
    </>
  );
};

export const Route = createFileRoute("/settings/profile/vehicles/$vehicleId")({
  component: VehicleEditPage,
});
